/**
 * De onde sai o iframe do chat de suporte e de quem e a sessao guardada nele.
 *
 * O front nao conhece a URL do portal nem a identidade da instancia: as duas
 * vem na assercao que o menu "Suporte" ja usa (`redirectTo` com o token no
 * fragmento). A primeira chamada da aba le dela a origem do portal e a chave
 * do usuario, e guarda so isso; o token fica em memoria por instantes, para o
 * caso de o iframe pedir a assercao logo em seguida.
 */

const TOKEN_FRAGMENT = '#token=';
const SECURE_PROTOCOL = 'https:';
const LOCAL_HOSTS = ['localhost', '127.0.0.1'];
const SESSION_KEY = 'support-widget-session-tc';
const HTTP_FORBIDDEN = 403;

/* A assercao vale 60 s; passado isto, pede-se outra em vez de arriscar. */
export const FRESH_ASSERTION_MS = 40 * 1000;

export const ASSERTION_FAILURE = {
  ACCESS_DENIED: 'accessDenied',
  UNAVAILABLE: 'unavailable'
};

/* So `iss` e `sub` interessam, e os dois sao ASCII: nao e preciso tratar os
   acentos do nome, que vem junto no corpo do token. */
function readClaims(token) {
  try {
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');

    return JSON.parse(window.atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
  } catch (error) {
    return null;
  }
}

function isTrustedPortal(url) {
  return url.protocol === SECURE_PROTOCOL || LOCAL_HOSTS.includes(url.hostname);
}

/**
 * Le a resposta de `/support/assertion`.
 * Devolve null quando ela nao traz portal e usuario utilizaveis.
 */
export function parseAssertionRedirect(redirectTo) {
  if (typeof redirectTo !== 'string' || !redirectTo.includes(TOKEN_FRAGMENT)) return null;

  const token = redirectTo.slice(redirectTo.indexOf(TOKEN_FRAGMENT) + TOKEN_FRAGMENT.length);
  const claims = readClaims(token);

  try {
    const url = new URL(redirectTo);

    if (!isTrustedPortal(url) || !claims?.iss || !claims?.sub) return null;

    return { portalOrigin: url.origin, userKey: `${claims.iss}:${claims.sub}`, token };
  } catch (error) {
    return null;
  }
}

/** Motivo da recusa, a partir do erro que o BaseApi rejeita. */
export function assertionFailure(error) {
  return error?.response?.status === HTTP_FORBIDDEN ? ASSERTION_FAILURE.ACCESS_DENIED : ASSERTION_FAILURE.UNAVAILABLE;
}

/**
 * Portal e chave do usuario ja conhecidos nesta aba. Em sessionStorage e por
 * id do usuario: vale ate a aba fechar ou alguem sair do sistema.
 */
export function readKnownSession(userId) {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(SESSION_KEY));
    const isSameUser = !!stored && String(stored.userId) === String(userId);

    return isSameUser && stored.portalOrigin && stored.userKey
      ? { portalOrigin: stored.portalOrigin, userKey: stored.userKey }
      : null;
  } catch (error) {
    return null;
  }
}

export function rememberSession(userId, session) {
  try {
    window.sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ userId, portalOrigin: session.portalOrigin, userKey: session.userKey })
    );
  } catch (error) {
    // eslint-disable-next-line no-empty
  }
}

export function forgetSession() {
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch (error) {
    // eslint-disable-next-line no-empty
  }
}

/** Pede a assercao ao TechCambio e devolve portal, chave do usuario e token. */
export async function requestAssertion(supportApi) {
  const { data } = await supportApi.getAssertion();

  return parseAssertionRedirect(data?.redirectTo);
}

/**
 * Entrada do portal completo com destino: o mesmo `redirectTo`, com o caminho
 * pedido antes do fragmento. So caminho do proprio portal; o resto e ignorado.
 */
export function withPortalDestination(redirectTo, destination) {
  const isPortalPath = typeof destination === 'string' && destination.startsWith('/') && !destination.startsWith('//');

  if (!isPortalPath || typeof redirectTo !== 'string' || !redirectTo.includes(TOKEN_FRAGMENT)) return redirectTo;

  const [address, token] = redirectTo.split(TOKEN_FRAGMENT);
  const separator = address.includes('?') ? '&' : '?';

  return `${address}${separator}destino=${encodeURIComponent(destination)}${TOKEN_FRAGMENT}${token}`;
}
